import React from 'react';
import Clappr from 'clappr';
import styled from 'styled-components';
import { useMixpanel } from 'react-mixpanel-browser';

const isiPhone = () => {
  // return false;
  try {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPod/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
};

const isHlsUrl = (u) => {
  if (!u) return false;
  if (typeof u === 'string') return /\.m3u8(\?|$)/i.test(u);
  if (u && u.type && /mpegurl|application\/vnd\.apple\.mpegurl/i.test(u.type)) return true;
  return /\.m3u8(\?|$)/i.test((u && u.src) || '');
};

const isMp4Url = (u) => {
  if (!u) return false;
  if (typeof u === 'string') return /\.(mp4|mov)(\?|$)/i.test(u);
  const t = (u && u.type) || '';
  return /video\/mp4|video\/quicktime/i.test(t) || /\.(mp4|mov)(\?|$)/i.test((u && u.src) || '');
};

const EDGE_GAP_SEC = 10;
const POST_JUMP_BUFFER_SEC = 3;
const NO_PROGRESS_MS = 9500;
const WATCHDOG_INTERVAL_MS = 9500;

const HEARTBEAT_MS = 9500;
const HEARTBEAT_EDGE_STALE_TICKS = 2;
const HEARTBEAT_COOLDOWN_MS = 9000;

const Player = ({ options, onError, globalPhone, endedExam, style, isLive }) => {
  const mixpanel = useMixpanel();
  const usingIPhone = React.useMemo(() => isiPhone(), []);
  const [renderer, setRenderer] = React.useState('clappr');

  const sources = (options && options.sources) || [];
  const getSrcAt = (i) => (sources && sources[i] && (sources[i].src ?? sources[i])) ?? null;

  const indexRef = React.useRef(0);
  const srcRef = React.useRef(null);

  const fallbackQueueRef = React.useRef([]);
  React.useEffect(() => {
    fallbackQueueRef.current = ((options && options.secondarySrcs) || []).slice();
  }, [options && options.secondarySrcs]);

  const hasNext = () => indexRef.current + 1 < sources.length && !!getSrcAt(indexRef.current + 1);
  const currentSrc = () => getSrcAt(indexRef.current);

  const playerRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const stateRef = React.useRef({
    lastProgressTs: 0,
    lastSeekableEnd: null,
    edgeStaleTicks: 0,
    watchdog: null,
    heartbeat: null,
    lastHeartbeatTs: 0,
    cleanup: null,
  });

  const [isReady, setIsReady] = React.useState(false);
  React.useEffect(() => setIsReady(true), []);

  React.useEffect(() => {
    if (!isReady) return;
    indexRef.current = getSrcAt(0) ? 0 : getSrcAt(1) ? 1 : 0;
    srcRef.current = currentSrc();

    let initial = 'clappr';
    if (usingIPhone && isHlsUrl(srcRef.current) && isLive) initial = 'native';
    if (isMp4Url(srcRef.current)) initial = 'clappr';
    setRenderer(initial);
  }, [isReady, usingIPhone, options && options.sources]);

  const trackUnloadPlayback = React.useCallback(() => {
    const player = playerRef.current;
    if (player && player.getCurrentTime) {
      const current = player.getCurrentTime && player.getCurrentTime();
      const duration = player.getDuration && player.getDuration();
      mixpanel.track('PLAYER_ASSISTA', {
        distinct_id: globalPhone,
        CURRENT_TIME_WATCH: current,
        DURATION_EXAM: duration,
      });
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    try {
      const current = el.currentTime;
      const duration = Number.isFinite(el.duration) ? el.duration : null;
      mixpanel.track('PLAYER_ASSISTA', {
        distinct_id: globalPhone,
        CURRENT_TIME_WATCH: current,
        DURATION_EXAM: duration,
      });
    } catch {}
  }, [globalPhone, mixpanel]);

  const sendPlayEvent = React.useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.dataLayer && window.dataLayer.push({
          event: window.location.href.includes('shared') ? 'shared_play_video' : 'play_video',
        });
      }
    } catch {}
    if (typeof window !== 'undefined' && window.location.href.includes('share')) {
      mixpanel.track('WEBAPP_PLAY_SHARED_VIDEO', { distinct_id: globalPhone });
    } else {
      mixpanel.track('WEBAPP_PLAY_VIDEO', { distinct_id: globalPhone });
    }
  }, [globalPhone, mixpanel]);

  React.useEffect(() => {
    if (!isReady || renderer !== 'native') return;

    const el = videoRef.current;
    if (!el) return;

    const startSrc = srcRef.current;
    if (!startSrc) return;

    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');
    el.setAttribute('disableRemotePlayback', '');
    el.autoplay = true;
    el.muted = true;
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';

    const isLiveHls = isHlsUrl(startSrc);

    const toggleCaptionsOnce = () => {
      try {
        const tts = el.textTracks;
        if (!tts || !tts.length) return false;
        let target = null;
        for (let i = 0; i < tts.length; i++) {
          const t = tts[i];
          const k = (t.kind || '').toLowerCase();
          if (k === 'subtitles' || k === 'captions') {
            if (t.mode === 'showing' || t.mode === 'hidden') {
              target = t;
              break;
            }
            if (!target) target = t;
          }
        }
        if (!target) return false;
        const original = target.mode;
        const temp = original === 'disabled' ? 'hidden' : 'disabled';
        target.mode = temp;
        setTimeout(() => {
          try {
            target.mode = original;
          } catch {}
        }, 200);
        return true;
      } catch {
        return false;
      }
    };

    const startPlayback = () => {
      try {
        el.src = startSrc;
        el.load();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch {}
    };

    const onUserInteract = () => {
      if (!el) return;
      if (el.muted) el.muted = false;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const jumpToLiveEdge = () => {
      if (!isLiveHls) return false;
      try {
        if (!el.seekable || el.seekable.length === 0) return false;
        const end = el.seekable.end(el.seekable.length - 1);
        if (!Number.isFinite(end)) return false;
        el.currentTime = Math.max(0, end - POST_JUMP_BUFFER_SEC);
        return true;
      } catch {
        return false;
      }
    };

    const onLoadedMetadata = () => {
      if (isLiveHls) jumpToLiveEdge();
    };

    const onEnded = () => {
      if (!hasNext()) {
        endedExam && endedExam(true);
        return;
      }
      indexRef.current += 1;
      srcRef.current = currentSrc();
      try {
        el.src = srcRef.current;
        el.load();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch {}
    };

    const loadNextFallback = () => {
      const next = fallbackQueueRef.current.shift();
      if (!next) return false;
      srcRef.current = next;
      try {
        el.src = srcRef.current;
        el.load();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
        return true;
      } catch {
        return false;
      }
    };

    const onErrorEvt = (event) => {
      const err = (event && event.target && event.target.error) || (event && event.error) || {};
      mixpanel.track('PLAYER_ERROR_ASSISTA', {
        distinct_id: globalPhone,
        PLAY_EXAM: false,
        ERROR_CODE: err && err.code,
        ERROR_TITLE: 'HTML5 Video Error',
        ERROR_LEVEL: 'FATAL',
        ERROR_ORIGIN: 'HTML5_VIDEO',
        ERROR_MESSAGE: (err && err.message) || String(err),
      });
      if (loadNextFallback()) return;
      onError && onError();
    };

    const onTimeUpdate = () => {
      stateRef.current.lastProgressTs = Date.now();
      try {
        if (el.seekable && el.seekable.length) {
          const end = el.seekable.end(el.seekable.length - 1);
          if (Number.isFinite(end)) {
            if (
              stateRef.current.lastSeekableEnd != null &&
              Math.abs(end - stateRef.current.lastSeekableEnd) < 0.01
            ) {
              stateRef.current.edgeStaleTicks = Math.min(99, stateRef.current.edgeStaleTicks + 1);
            } else {
              stateRef.current.edgeStaleTicks = 0;
            }
            stateRef.current.lastSeekableEnd = end;
            const behind = end - el.currentTime;
            if (behind > EDGE_GAP_SEC) jumpToLiveEdge();
          }
        }
      } catch {}
    };

    const onStallLike = () => {
      console.log("stalled!", el.currentTime);
      // const kicked = toggleCaptionsOnce();
      // if (!kicked) {
      //   try {
      //     el.currentTime = Math.max(0, el.currentTime + 0.001);
      //   } catch {}
      // }
    };

    const startWatchdog = () => {
      stateRef.current.lastProgressTs = Date.now();
      stateRef.current.watchdog = setInterval(() => {
        const noProgress = Date.now() - (stateRef.current.lastProgressTs || 0) > NO_PROGRESS_MS;
        const lowReady = (el.readyState || 0) < 3;
        if (noProgress || lowReady) {
          if (!jumpToLiveEdge()) toggleCaptionsOnce();
        }
      }, WATCHDOG_INTERVAL_MS);
    };

    const startHeartbeat = () => {
      stateRef.current.heartbeat = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        let should = true;
        if (stateRef.current.edgeStaleTicks < HEARTBEAT_EDGE_STALE_TICKS) should = false;
        if (Date.now() - (stateRef.current.lastProgressTs || 0) <= NO_PROGRESS_MS) should = should || false;
        if (Date.now() - stateRef.current.lastHeartbeatTs < HEARTBEAT_COOLDOWN_MS) should = false;
        if (!should) return;
        const kicked = toggleCaptionsOnce();
        if (kicked) stateRef.current.lastHeartbeatTs = Date.now();
      }, HEARTBEAT_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        jumpToLiveEdge();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    };

    const touchOpts = { passive: true };

    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('waiting', onStallLike);
    el.addEventListener('stalled', onStallLike);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onErrorEvt);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('touchend', onUserInteract, touchOpts);
    window.addEventListener('beforeunload', trackUnloadPlayback);

    startPlayback();
    startWatchdog();
    startHeartbeat();
    sendPlayEvent();

    stateRef.current.cleanup = () => {
      clearInterval(stateRef.current.watchdog);
      clearInterval(stateRef.current.heartbeat);
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('waiting', onStallLike);
      el.removeEventListener('stalled', onStallLike);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onErrorEvt);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('touchend', onUserInteract, touchOpts);
      window.removeEventListener('beforeunload', trackUnloadPlayback);
      stateRef.current.watchdog = null;
      stateRef.current.heartbeat = null;
    };

    return () => {
      try {
        stateRef.current.cleanup && stateRef.current.cleanup();
      } catch {}
    };
  }, [isReady, renderer, endedExam, onError, sendPlayEvent, trackUnloadPlayback, globalPhone, mixpanel]);

  React.useEffect(() => {
    if (!isReady || renderer !== 'clappr') return;
    srcRef.current = currentSrc();
    if (!srcRef.current) return;

    const player = new Clappr.Player({
      parentId: '#player',
      source: srcRef.current,
      width: '100%',
      height: '100%',
      playback: { playInline: true, preload: 'auto' },
      events: {
        onEnded: () => playNextVideoClappr(),
        onError: (event) => {
          const e = event || {};
          const code = e.code;
          const UI = e.UI;
          const level = e.level;
          const origin = e.origin;

          mixpanel.track('PLAYER_ERROR_ASSISTA', {
            distinct_id: globalPhone,
            PLAY_EXAM: false,
            ERROR_CODE: code,
            ERROR_TITLE: UI && UI.title,
            ERROR_LEVEL: level,
            ERROR_ORIGIN: origin,
            ERROR_MESSAGE: UI && UI.message,
          });
          const next = fallbackQueueRef.current.shift();
          if (next) {
            srcRef.current = next;
            try {
              player.load({ source: next });
              player.play();
              return;
            } catch {}
          }
          onError && onError();
        },
      },
    });

    playerRef.current = player;
    player.once(Clappr.Events.PLAYER_PLAY, () => {
      sendPlayEvent();
    });
    player.play();
    window.addEventListener('beforeunload', trackUnloadPlayback);

    function playNextVideoClappr() {
      if (!hasNext()) {
        endedExam && endedExam(true);
        return;
      }
      const nextIdx = indexRef.current + 1;
      const nextSrc = getSrcAt(nextIdx);
      if (!nextSrc) {
        onError && onError();
        return;
      }
      if (usingIPhone && isHlsUrl(nextSrc) && isLive) {
        indexRef.current = nextIdx;
        srcRef.current = nextSrc;
        try {
          if (playerRef.current && playerRef.current.destroy) {
            playerRef.current.destroy();
          }
        } catch {}
        playerRef.current = null;
        setRenderer('native');
        return;
      }
      indexRef.current = nextIdx;
      srcRef.current = nextSrc;
      player.load({ source: nextSrc });
      player.play();
    }

    return () => {
      window.removeEventListener('beforeunload', trackUnloadPlayback);
      try {
        player.destroy();
      } catch {}
      playerRef.current = null;
    };
  }, [isReady, renderer, onError, endedExam, sendPlayEvent, trackUnloadPlayback, mixpanel, globalPhone, usingIPhone]);

  return (
    <ClapprPlayer id="player" style={{ width: '100%', ...(style || {}) }}>
      {renderer === 'native' && (
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%' }}
          controls
          playsInline
          disableRemotePlayback
          crossOrigin="anonymous"
        />
      )}
    </ClapprPlayer>
  );
};

export default Player;

const ClapprPlayer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: transparent;
`;
